import { AIEngine } from './AIEngine';
import type {
  GameSession,
  WorldState,
  Character,
  PlayerChoice,
  NarrativeEvent,
  CausalEvent
} from '../types/game';

export class GameManager {
  private aiEngine: AIEngine;
  private session: GameSession | null = null;

  constructor() {
    this.aiEngine = new AIEngine();
  }

  /**
   * Start new game
   */
  async startNewGame(playerPreferences?: {
    characterName?: string;
    characterTraits?: string;
    worldTheme?: string;
    worldDescription?: string;
  }): Promise<GameSession> {
    try {
      console.log('[GameManager] Starting new game generation...');
      if (playerPreferences) {
        console.log('[GameManager] Player preferences:', playerPreferences);
      }

      // 1. Generate world
      console.log('[GameManager] Generating world settings...');
      const world = await this.aiEngine.generateWorld(
        playerPreferences?.worldTheme,
        playerPreferences?.worldDescription
      );
      console.log('[GameManager] World generation complete:', world.name);

      // 2. Generate player character identity
      console.log('[GameManager] Generating player character identity...');
      const playerIdentity = await this.aiEngine.generatePlayerIdentity(
        world,
        playerPreferences?.characterName,
        playerPreferences?.characterTraits
      );
      console.log('[GameManager] Player identity generation complete');

      // 3. Initialize world state
      const worldState: WorldState = {
        tension: 50, // Initial tension
        majorEvents: [],
        unresolvedConflicts: [world.conflict],
        worldChanges: [],
        turnCount: 0,
        keyCharacterFates: [] // Initialize character fate tracking
      };

      // 4. Generate characters
      console.log('[GameManager] Generating initial characters...');
      const characters = await this.aiEngine.generateInitialCharacters(world);
      console.log('[GameManager] Character generation complete, total', characters.length, 'characters');

      // Initialize character fate tracking
      worldState.keyCharacterFates = characters.map(char => ({
        characterId: char.id,
        status: 'alive' as const,
        influence: 50 // Initial influence
      }));

      // 5. Generate opening event
      console.log('[GameManager] Generating opening event...');
      const openingEvent = await this.aiEngine.generateOpeningEvent(world, characters);
      console.log('[GameManager] Opening event generation complete');

      // 6. Create game session
      this.session = {
        sessionId: `session_${Date.now()}`,
        world,
        worldState,
        characters,
        causalChain: [],
        currentEvent: openingEvent,
        playerHistory: [],
        isEnded: false,
        playerIdentity // Add player identity
      };

      console.log('[GameManager] Game initialization complete!');
      return this.session;
    } catch (error) {
      console.error('[GameManager] Game initialization failed:', error);
      throw new Error(
        error instanceof Error
          ? `Initialization failed: ${error.message}`
          : 'Unknown error occurred during game initialization'
      );
    }
  }

  /**
   * Execute player choice
   */
  async makeChoice(choiceId: string): Promise<{
    success: boolean;
    causalEvent: CausalEvent;
    nextEvent: NarrativeEvent | null;
    session: GameSession;
  }> {
    if (!this.session || !this.session.currentEvent) {
      throw new Error('No active game session');
    }

    const choice = this.session.currentEvent.choices.find(c => c.id === choiceId);
    if (!choice) {
      throw new Error('Invalid choice ID');
    }

    // 1. Process choice
    const result = await this.aiEngine.processChoice(
      choice,
      this.session.currentEvent,
      this.session.worldState,
      this.session.characters
    );

    // 2. Update NPC memory and plans
    const charactersWithMemory = await this.aiEngine.updateNPCMemoryAndPlans(
      result.causalEvent.affectedCharacters,
      choice.text,
      result.causalEvent,
      result.updatedCharacters
    );

    // 3. Update session state
    this.session.characters = charactersWithMemory;
    this.session.causalChain.push(result.causalEvent);
    this.session.playerHistory.push(choice.text);

    // 3. Update world state
    const changes = result.worldStateChanges;
    this.session.worldState.turnCount += 1;
    this.session.worldState.tension = Math.max(
      0,
      Math.min(100, this.session.worldState.tension + (changes.tensionChange || 0))
    );

    if (changes.majorEvent) {
      this.session.worldState.majorEvents.push(changes.majorEvent);
    }

    if (changes.newConflicts) {
      this.session.worldState.unresolvedConflicts.push(...changes.newConflicts);
    }

    if (changes.resolvedConflicts) {
      this.session.worldState.unresolvedConflicts =
        this.session.worldState.unresolvedConflicts.filter(
          c => !changes.resolvedConflicts!.includes(c)
        );
    }

    // 4. Check if ending should be triggered
    const endingCheck = await this.aiEngine.checkForEnding(
      this.session.worldState,
      this.session.characters
    );

    if (endingCheck.shouldEnd) {
      this.session.isEnded = true;
      this.session.endingType = endingCheck.endingType;
      this.session.currentEvent = {
        id: 'ending',
        title: 'Ending',
        description: endingCheck.endingNarrative || '',
        choices: [],
        timestamp: Date.now()
      };

      return {
        success: true,
        causalEvent: result.causalEvent,
        nextEvent: null,
        session: this.session
      };
    }

    // 5. Check NPC proactive actions (triggered at specific turns)
    const npcEvent = await this.aiEngine.checkNPCProactiveActions(
      this.session.characters,
      this.session.worldState,
      this.session.playerHistory
    );

    // 6. Set next event (may be regular event or NPC proactive event)
    this.session.currentEvent = npcEvent || result.nextEvent;

    return {
      success: true,
      causalEvent: result.causalEvent,
      nextEvent: this.session.currentEvent,
      session: this.session
    };
  }

  /**
   * Get current session
   */
  getSession(): GameSession | null {
    return this.session;
  }

  /**
   * Get game statistics
   */
  getGameStats(): {
    turns: number;
    aliveCharacters: number;
    totalCharacters: number;
    avgRelationship: number;
    tension: number;
  } | null {
    if (!this.session) return null;

    const aliveCharacters = this.session.characters.filter(c => c.alive).length;
    const avgRelationship =
      this.session.characters.reduce((sum, c) => sum + c.relationship, 0) /
      this.session.characters.length;

    return {
      turns: this.session.worldState.turnCount,
      aliveCharacters,
      totalCharacters: this.session.characters.length,
      avgRelationship: Math.round(avgRelationship),
      tension: this.session.worldState.tension
    };
  }

  /**
   * Get AI engine conversation history statistics
   * For monitoring Token usage
   */
  getAIStats(): {
    messageCount: number;
    estimatedTokens: number;
    compressionNeeded: boolean;
  } {
    return this.aiEngine.getHistoryStats();
  }
}
